import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SEO, getBreadcrumbSchema } from '@/components/seo/SEO';
import { 
  FloralCorner, 
  FloralSeparator 
} from '@/components/ui/FloralElements';
import { 
  Camera,
  X,
  ChevronLeft,
  ChevronRight,
  Facebook
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

// Ranch photos from user uploads
const ranchPhotos = {
  calves: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/9exx0j39_IMG_2561.JPEG',
  horses: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/m973uo9g_IMG_2599.jpg',
  cowCalf: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/uc00byr0_IMG_3232.JPEG',
  herd: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/enbbzva9_IMG_4574.jpeg',
  goatKid: 'https://customer-assets.emergentagent.com/job_0f922ffd-6fd3-4884-9c25-9862f533b31c/artifacts/ybcfo7j1_IMG_6810.jpeg',
  // New photos
  womanGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/n5mw12s5_IMG_6869.JPG',
  childGoat: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/1ejb7upe_IMG_7758.JPG',
  aurora: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/h2mpppe9_IMG_8669.jpeg',
  apron: 'https://customer-assets.emergentagent.com/job_floral-upgrade-1/artifacts/xuuv78u0_0%203.21.09%E2%80%AFPM.jpg',
};

// Gallery images with SEO-friendly captions and alt text
const galleryImages = [
  {
    id: 1,
    src: ranchPhotos.womanGoat,
    alt: 'Ranch owner cuddling with a baby goat on our Nebraska family homestead - showing the love and care we give our animals',
    caption: 'Goat Cuddles',
    description: 'The love between our family and our goats - they are truly part of the family'
  },
  {
    id: 2,
    src: ranchPhotos.childGoat,
    alt: 'Ranch child bottle feeding a baby goat in the barn - next generation learning sustainable agriculture',
    caption: 'Bottle Feeding Time',
    description: 'Our kids learn from an early age how to care for our animals with love'
  },
  {
    id: 3,
    src: ranchPhotos.aurora,
    alt: 'Stunning aurora borealis over our Nebraska ranch barn - beautiful night sky photography',
    caption: 'Nebraska Night Sky',
    description: 'Magical moments on the ranch - the aurora over our barn'
  },
  {
    id: 4,
    src: ranchPhotos.apron,
    alt: 'Handmade apron with jars of homemade canned goods - jams, jellies, and preserves from our garden',
    caption: 'Canning Season',
    description: 'Our handmade aprons and homemade preserves - from our garden to your table'
  },
  {
    id: 5,
    src: ranchPhotos.goatKid,
    alt: 'Ranch child feeding a baby goat on our Nebraska family homestead - showing the gentle care and bond between our family and animals',
    caption: 'Next Generation Ranch Life',
    description: 'Our kids learning to care for the animals, just like generations before them'
  },
  {
    id: 6,
    src: ranchPhotos.calves,
    alt: 'Young rancher kneeling with black Angus calves in a straw-bedded barn on our working cattle ranch',
    caption: 'Calf Season on the Ranch',
    description: 'The next generation helping with our cow-calf operation during calving season'
  },
  {
    id: 7,
    src: ranchPhotos.horses,
    alt: 'Family horseback riding on the ranch with broodmares - practicing sustainable agriculture and traditional ranching methods',
    caption: 'Horseback Ranching',
    description: 'Working with our broodmares, keeping traditional ranching methods alive'
  },
  {
    id: 8,
    src: ranchPhotos.cowCalf,
    alt: 'Mother cow with newborn calf in the Nebraska pasture - ethical animal husbandry and sustainable cattle ranching',
    caption: 'New Life on the Pasture',
    description: 'A proud mama with her newborn calf on our Nebraska pastures'
  },
  {
    id: 9,
    src: ranchPhotos.herd,
    alt: 'Black Angus cattle herd being moved across the Nebraska hills with our ranch dog watching - sustainable grazing practices',
    caption: 'Moving the Herd',
    description: 'Our faithful ranch dog watching over the herd during a cattle drive'
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const GalleryPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Gallery', path: '/gallery' }
  ];

  const openLightbox = (image, index) => {
    setSelectedImage(image);
    setCurrentIndex(index);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const nextIndex = (currentIndex + 1) % galleryImages.length;
    setCurrentIndex(nextIndex);
    setSelectedImage(galleryImages[nextIndex]);
  };

  const prevImage = () => {
    const prevIndex = (currentIndex - 1 + galleryImages.length) % galleryImages.length;
    setCurrentIndex(prevIndex);
    setSelectedImage(galleryImages[prevIndex]);
  };

  return (
    <>
      <SEO
        title="Photo Gallery"
        description="Explore our Nebraska ranch photo gallery featuring goats, cattle, horses, ranch life, and beautiful Nebraska landscapes. See the family behind Little House on the Table."
        path="/gallery"
        structuredData={getBreadcrumbSchema(breadcrumbs)}
        keywords="ranch photos, Nebraska farm pictures, goat photos, cattle ranch images, farm life gallery, rural Nebraska photography"
      />
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-hero overflow-hidden">
        <FloralCorner position="top-left" className="opacity-40" />
        <FloralCorner position="bottom-right" className="opacity-40" />
        
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="max-w-3xl mx-auto"
          >
            <motion.span 
              variants={fadeInUp}
              className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4"
            >
              <Camera className="h-4 w-4" />
              Ranch Life
            </motion.span>
            <motion.h1 
              variants={fadeInUp}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6"
            >
              Gallery
            </motion.h1>
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-muted-foreground"
            >
              Glimpses of daily life on our 7th generation Nebraska family ranch. 
              Sustainable agriculture, ethical animal care, and generations of tradition.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-20 relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {galleryImages.map((image, index) => (
              <motion.div
                key={image.id}
                variants={fadeInUp}
                className={`${index === 0 || index === 2 ? 'sm:col-span-2 lg:col-span-2' : ''}`}
              >
                <button
                  onClick={() => openLightbox(image, index)}
                  className="block w-full group cursor-pointer"
                >
                  <div className="raised-card overflow-hidden rounded-xl">
                    <div className="relative overflow-hidden">
                      <img
                        src={image.src}
                        alt={image.alt}
                        loading="lazy"
                        className={`w-full object-cover transition-transform duration-700 group-hover:scale-105 ${
                          index === 0 || index === 2 ? 'h-72 md:h-96' : 'h-64 md:h-72'
                        }`}
                      />
                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-foreground/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6">
                          <h3 className="font-serif text-lg md:text-xl font-semibold text-white mb-1">
                            {image.caption}
                          </h3>
                          <p className="text-white/80 text-sm">
                            {image.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </button>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Lightbox Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={closeLightbox}>
        <DialogContent className="max-w-5xl w-full p-0 bg-foreground/95 border-0">
          <DialogTitle className="sr-only">
            {selectedImage?.caption || 'Gallery Image'}
          </DialogTitle>
          <AnimatePresence mode="wait">
            {selectedImage && (
              <motion.div
                key={selectedImage.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="relative"
              >
                {/* Close button */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={closeLightbox}
                  className="absolute top-4 right-4 z-10 text-white hover:bg-white/20"
                >
                  <X className="h-6 w-6" />
                </Button>

                {/* Navigation buttons */}
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevImage}
                  className="absolute left-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                >
                  <ChevronLeft className="h-8 w-8" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextImage}
                  className="absolute right-4 top-1/2 -translate-y-1/2 z-10 text-white hover:bg-white/20"
                >
                  <ChevronRight className="h-8 w-8" />
                </Button>

                {/* Image */}
                <img
                  src={selectedImage.src}
                  alt={selectedImage.alt}
                  className="w-full max-h-[80vh] object-contain"
                />

                {/* Caption */}
                <div className="p-6 bg-foreground">
                  <h3 className="font-serif text-xl font-semibold text-white mb-2">
                    {selectedImage.caption}
                  </h3>
                  <p className="text-white/70">
                    {selectedImage.description}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </DialogContent>
      </Dialog>

      <FloralSeparator />

      {/* Info Section */}
      <section className="py-20 bg-secondary relative">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-3xl mx-auto text-center"
          >
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-foreground mb-4">
              Follow Our Journey
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Want to see more of our daily ranch life? Follow us on Facebook for 
              behind-the-scenes moments, new product announcements, and stories from 
              our Nebraska homestead.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Button
                asChild
                size="lg"
                className="btn-beveled px-8 py-6"
              >
                <a
                  href="https://www.facebook.com/LittleHouseontheTable"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Facebook className="h-5 w-5" />
                  Follow on Facebook
                </a>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default GalleryPage;
